/**
 * Выборка точек кривой для SVG.
 *
 * Частота выборки определяется кривизной в пикселях, а не шагом по `x`:
 * постоянный шаг одновременно рвёт вершину параболы и впустую дробит её
 * прямые крылья.
 *
 * Отдельная забота — разрыв гиперболы. Ветви по разные стороны `x = h`
 * никогда не попадают в один path: соединённые, они рисуют вертикальную
 * черту через всю плоскость, и ученик видит «ещё одну линию», которой нет.
 *
 * Окружность и эллипс — не функции, по `x` их не пройти: одному `x` отвечают
 * две точки. Их выборка идёт по углу, тем же адаптивным делением — отклонение
 * хорды меряется на экране, и ему всё равно, что за параметр под ним.
 */

import type { FunctionCurve } from "./model";
import { toNumber } from "./numbers";
import { discontinuityAt, valueAt } from "./math";
import { toScreen, worldBounds, type PlotCamera, type PlotPoint, type PlotSize } from "./viewport";

/** Допустимое отклонение хорды от кривой, пиксели. */
const TOLERANCE = 0.4;
const MAX_DEPTH = 16;
const MAX_POINTS = 4000;
/** Стартовое равномерное дробление — страховка от симметрии. */
const SEEDS = 16;

type Sample = PlotPoint | null;

function deviation(a: PlotPoint, middle: PlotPoint, b: PlotPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) return Math.hypot(middle.x - a.x, middle.y - a.y);
  return Math.abs(dy * (middle.x - a.x) - dx * (middle.y - a.y)) / length;
}

/** Путь по параметру: `at(t)` — точка кривой, `t` пробегает [from; to]. */
function collect(
  at: (x: number) => Sample,
  from: number,
  to: number,
  out: Sample[],
): void {
  const first = at(from);
  out.push(first);

  const walk = (xa: number, pa: Sample, xb: number, pb: Sample, depth: number): void => {
    if (depth >= MAX_DEPTH || out.length >= MAX_POINTS) {
      out.push(pb);
      return;
    }
    const xm = (xa + xb) / 2;
    const pm = at(xm);
    if (pa && pb && pm && deviation(pa, pm, pb) <= TOLERANCE) {
      out.push(pb);
      return;
    }
    walk(xa, pa, xm, pm, depth + 1);
    walk(xm, pm, xb, pb, depth + 1);
  };

  let previousX = from;
  let previous = first;
  for (let seed = 1; seed <= SEEDS; seed++) {
    const nextX = from + ((to - from) * seed) / SEEDS;
    const next = at(nextX);
    walk(previousX, previous, nextX, next, 0);
    previousX = nextX;
    previous = next;
  }
}

/**
 * Разрыв путей на границе рабочей полосы.
 *
 * Полоса — экран плюс по экрану с каждой стороны. Точка за полосой попадает в
 * путь один раз, чтобы штрих ушёл за край, и на ней путь заканчивается: так
 * в `d` не уезжают координаты порядка 10^9 и не рисуется отрезок в никуда.
 */
function clip(points: Sample[], size: PlotSize): PlotPoint[][] {
  const inside = (point: PlotPoint) =>
    point.x >= -size.width &&
    point.x <= size.width * 2 &&
    point.y >= -size.height &&
    point.y <= size.height * 2;

  const paths: PlotPoint[][] = [];
  let current: PlotPoint[] = [];
  let pending: PlotPoint | null = null;
  const flush = () => {
    if (current.length >= 2) paths.push(current);
    current = [];
  };

  for (const point of points) {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      flush();
      pending = null;
      continue;
    }
    if (inside(point)) {
      if (pending) {
        current.push(pending);
        pending = null;
      }
      current.push(point);
    } else {
      if (current.length > 0) {
        current.push(point);
        flush();
      }
      pending = point;
    }
  }
  flush();
  return paths;
}

/** Замкнутый овал по углу: x = h + rx·cos t, y = k + ry·sin t. */
function sampleOval(
  h: number, k: number, rx: number, ry: number, camera: PlotCamera, size: PlotSize,
): PlotPoint[][] {
  if (rx === 0 || ry === 0) return [];
  const samples: Sample[] = [];
  collect(
    (t) => toScreen(camera, size, { x: h + rx * Math.cos(t), y: k + ry * Math.sin(t) }),
    0, 2 * Math.PI, samples,
  );
  return clip(samples, size);
}

/**
 * Экранные пути кривой. Несколько путей — либо разрыв гиперболы, либо уход
 * графика за рабочую полосу и возвращение обратно.
 */
export function sampleCurve(
  curve: FunctionCurve,
  camera: PlotCamera,
  size: PlotSize,
): PlotPoint[][] {
  if (size.width <= 0 || size.height <= 0) return [];
  if (curve.kind === "circle") {
    const r = Math.abs(toNumber(curve.r));
    return sampleOval(toNumber(curve.h), toNumber(curve.k), r, r, camera, size);
  }
  if (curve.kind === "ellipse") {
    return sampleOval(
      toNumber(curve.h), toNumber(curve.k),
      Math.abs(toNumber(curve.a)), Math.abs(toNumber(curve.b)), camera, size,
    );
  }

  const bounds = worldBounds(camera, size);
  const margin = (bounds.maxX - bounds.minX) * 0.02;
  let from = bounds.minX - margin;
  const to = bounds.maxX + margin;

  const at = (x: number): Sample => {
    const y = valueAt(curve, x);
    if (y === null || !Number.isFinite(y)) return null;
    return toScreen(camera, size, { x, y });
  };

  // Корень начинается ровно в x = h. Выборка от левого края нашла бы начало
  // только с точностью до шага, и график висел бы в воздухе рядом с точкой
  // «Начало графика».
  if (curve.kind === "sqrt") {
    const start = toNumber(curve.h);
    if (start >= to) return [];
    from = Math.max(from, start);
  }

  // Излом модуля — ровно в x = h. Хорда через излом не проходит проверку
  // отклонения ни на какой глубине, и без разбиения угол скругляется.
  if (curve.kind === "abs") {
    const kink = toNumber(curve.h);
    if (kink > from && kink < to) {
      const samples: Sample[] = [];
      const rest: Sample[] = [];
      collect(at, from, kink, samples);
      collect(at, kink, to, rest);
      return clip([...samples, ...rest.slice(1)], size);
    }
  }

  const breakAt = discontinuityAt(curve);
  if (breakAt === null || breakAt <= from || breakAt >= to) {
    const samples: Sample[] = [];
    collect(at, from, to, samples);
    return clip(samples, size);
  }

  // Отступ от асимптоты подбирается так, чтобы последняя точка ветви уже
  // вышла за рабочую полосу: тогда штрих уходит за край, а не обрывается в
  // воздухе на полпути.
  const bandHeight = (size.height * 3) / camera.scale;
  const strength = Math.abs(curve.kind === "reciprocal" ? curve.a.numerator / curve.a.denominator : 1);
  const epsilon = Math.min(
    (to - from) / 8,
    Math.max(Math.abs(breakAt) * 1e-9 + 1e-12, strength / Math.max(bandHeight, 1e-9)),
  );

  const left: Sample[] = [];
  const right: Sample[] = [];
  collect(at, from, breakAt - epsilon, left);
  collect(at, breakAt + epsilon, to, right);
  return [...clip(left, size), ...clip(right, size)];
}

/** Данные для атрибута `d`. Нечисловые координаты в путь не попадают. */
export function toPathData(path: PlotPoint[]): string {
  const usable = path.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (usable.length < 2) return "";
  return usable
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    .join(" ");
}
